/**
 * Credit Warning Hook
 *
 * Manages credit warning state, toast notifications, and pre-action credit checks.
 * Automatically shows toast notifications once per session when credits are low/critical.
 */

import { useEffect } from 'react';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  getCreditWarningLevel,
  getCreditWarningMessage,
  canAffordAction,
  type CreditWarningLevel,
  type CreditWarningInfo
} from '@/utils/creditWarnings';

export interface UseCreditWarningsReturn {
  warningLevel: CreditWarningLevel;
  warningInfo: CreditWarningInfo | null;
  checkBeforeAction: (required: number) => boolean;
  warnBeforeAction: (required: number) => void;
}

/**
 * Hook to manage credit warnings and notifications
 *
 * Features:
 * - Automatically shows toast notification once per session for low/critical credits
 * - Provides functions to check credit sufficiency before actions
 * - Returns current warning level and message info
 *
 * @returns Credit warning state and utility functions
 */
export function useCreditWarnings(): UseCreditWarningsReturn {
  const { totalAvailableCredits, isLoading, isEnabled } = usePractitionerCredits();
  const { hasPractitionerAccess } = useUserRole();
  const { user } = useAuth();
  const { toast } = useToast();

  // Don't show warnings while data is loading or if user is not authenticated
  // (credits default to 0 when query is disabled during sign-out)
  const shouldSuppress = isLoading || !user || !isEnabled;
  const warningLevel = shouldSuppress ? 'healthy' : getCreditWarningLevel(totalAvailableCredits);
  const userType = hasPractitionerAccess ? 'practitioner' : 'individual';
  const warningInfo = shouldSuppress ? null : getCreditWarningMessage(totalAvailableCredits, userType);

  // Show toast once per session when low/critical
  useEffect(() => {
    if (!warningInfo || shouldSuppress) return;

    const toastKey = `credit_warning_toast_${warningLevel}`;
    const toastShown = sessionStorage.getItem(toastKey);

    if (!toastShown) {
      toast({
        title: warningInfo.title,
        description: warningInfo.message,
        variant: warningInfo.variant,
        duration: 6000,
      });
      sessionStorage.setItem(toastKey, 'true');
    }
  }, [warningLevel, warningInfo, toast]);

  /**
   * Check if user has enough credits before performing an action
   * Shows error toast if insufficient
   *
   * @param required - Number of credits required
   * @returns True if user can afford the action
   */
  const checkBeforeAction = (required: number): boolean => {
    if (!canAffordAction(totalAvailableCredits, required)) {
      toast({
        title: 'Insufficient Credits',
        description: `You need ${required} credits but only have ${totalAvailableCredits}. Please purchase more credits to continue.`,
        variant: 'destructive',
        duration: 8000,
      });
      return false;
    }
    return true;
  };

  /**
   * Warn user if they'll have low credits after an action
   * Only shows warning, doesn't block the action
   *
   * @param required - Number of credits required
   */
  const warnBeforeAction = (required: number): void => {
    const remainingAfter = totalAvailableCredits - required;

    // Warn if they'll have less than 10 credits remaining
    if (remainingAfter >= 0 && remainingAfter < 10) {
      toast({
        title: 'Low Credits After This Action',
        description: `You'll have ${remainingAfter} credits remaining after this action. Consider purchasing more to avoid interruptions.`,
        variant: 'warning',
        duration: 6000,
      });
    }
  };

  return {
    warningLevel,
    warningInfo,
    checkBeforeAction,
    warnBeforeAction,
  };
}
